import { useTraduccion } from '@tallerya/i18n';
import { Boton, useAvisos, Ventana } from '@tallerya/ui';
import { useState } from 'react';
import { DesactivarItem } from './DesactivarItem';
import { EliminarItem } from './EliminarItem';
import { useFichaItem, type FichaItem } from './datos';

// Las confirmaciones del menú de la fila del listado: Desactivar y Eliminar.
// La fila no trae en cuántas órdenes está el ítem —el listado no lo cuenta, sería
// una subconsulta por fila—, así que se pide la ficha al abrir, que sí lo trae.
// Activar no pasa por aquí: se deshace solo, y no hay nada que advertir.

export type AccionItem = 'desactivar' | 'eliminar';

interface Props {
  pedido: { accion: AccionItem; id: string } | null;
  onCerrar: () => void;
  /** Lo que se hizo, después de hacerlo: la ficha sale al listado si el ítem se eliminó. */
  onHecho?: (accion: AccionItem) => void;
}

export function ConfirmacionItem({ pedido, onCerrar, onHecho }: Props) {
  // La clave reinicia todo al abrirla para otro ítem.
  return pedido ? (
    <Contenido key={pedido.id} id={pedido.id} inicial={pedido.accion} onCerrar={onCerrar} {...(onHecho && { onHecho })} />
  ) : null;
}

function Contenido({
  id,
  inicial,
  onCerrar,
  onHecho,
}: {
  id: string;
  inicial: AccionItem;
  onCerrar: () => void;
  onHecho?: (accion: AccionItem) => void;
}) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const consulta = useFichaItem(id);
  // «Eliminar» puede pasar a «Desactivar» cuando el ítem ya se usó.
  const [accion, setAccion] = useState(inicial);

  // La última ficha que se vio. Al eliminar, la caché se refresca antes de
  // cerrar y la ficha pasa a «no existe»: sin esto, la ventana parpadearía con
  // ese aviso justo antes de irse.
  const [vista, setVista] = useState<FichaItem | null>(null);
  if (consulta.data && consulta.data !== vista) setVista(consulta.data);
  const ficha = consulta.data ?? vista;

  if (!ficha) {
    return (
      <Ventana
        abierta
        titulo={t(accion === 'eliminar' ? 'admin.catalogo.menu.eliminar' : 'admin.catalogo.menu.desactivar')}
        descripcion={consulta.isPending ? t('admin.catalogo.confirmar.cargando') : t('admin.catalogo.confirmar.error')}
        acciones={
          <Boton variante="secundario" onClick={onCerrar}>
            {t('admin.catalogo.confirmar.cerrar')}
          </Boton>
        }
      />
    );
  }

  const listo = (titulo: string) => {
    avisar({ titulo });
    onCerrar();
    onHecho?.(accion);
  };

  return accion === 'desactivar' ? (
    <DesactivarItem
      ficha={ficha}
      onCerrar={onCerrar}
      onDesactivado={() => listo(t('admin.catalogo.aviso.desactivado', { nombre: ficha.name }))}
    />
  ) : (
    <EliminarItem
      ficha={ficha}
      onCerrar={onCerrar}
      onEliminado={() => listo(t('admin.catalogo.aviso.eliminado', { nombre: ficha.name }))}
      onDesactivar={() => setAccion('desactivar')}
    />
  );
}
